import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, Route, RouteProps, useRouteMatch } from "react-router-dom";
import FullScreenLoader from "../../Components/FullScreenLoader";
import ResourceMeta from "../../Components/ResourceMeta";
import DomainNoAccessDialog from "../../Dialogs/DomainNoAccessDialog";
import useAsyncTask from "../../Hooks/useAsyncTask";
import DomainUtils from "../../Models/Domain";
import { TZine } from "../../Models/Zine/@types";
import { useStoreActions, useStoreState } from "../../Stores";
import Query from "../../Stores/Agency/Query";
import AuthRecommender from "../Auth/AuthRecommender";
import EntityPreview from "../EntityPreview";
import WithEntityRegistration from "../EntityRegistration/WithEntityRegistration";
import { useRegistrantHandler } from "../PrivateRoute/useRegistrantHandler";

export interface ZineRouteProps extends RouteProps {}
export const getUrlWithRegionFilter = (baseUrl: string, regions: string[]) => {
    if (!regions.length) return baseUrl;
    else return baseUrl + `?filter=${Query.getQueryFromObject({ regions: regions[0] })}`;
};
const ZineRoute: FC<ZineRouteProps> = (routeProps) => {
    const {
        params: { id: zineId },
    } = useRouteMatch<{ id: string }>();
    const [isDomainAllowed, setIsDomainAllowed] = useState<boolean>(false);
    const {
        loadZine: getZine,
        setZine,
        setAppliedFilter,
        setGuestInfo,
    } = useStoreActions(({ Zine: { loadZine, setZine }, Agency: { setAppliedFilter }, User: { setGuestInfo } }) => ({
        setGuestInfo,
        loadZine,
        setZine,
        setAppliedFilter,
    }));
    const { user, zine, guestInfo, agency } = useStoreState(({ User: { user, guestInfo }, Zine: { zine }, Agency: { agency } }) => ({
        agency,
        user,
        guestInfo,
        zine,
    }));
    const { isAuthenticated, AuthComponent } = useRegistrantHandler({ initRegistrant: true, baseUrl: getUrlWithRegionFilter("/resources", []) });
    const checkDomains = useCallback(
        (data: TZine) => {
            return DomainUtils.checkEmailDomains(
                data.restrictedStrategicPartners || [],
                {
                    domainRestrictionType: data.domainRestrictionType,
                    restrictedEmailDomains: data.restrictedEmailDomains || [],
                    restrictedStrategicPartnerIds: data.restrictedStrategicPartnerIds || [],
                },
                user?.email
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.email]
    );
    const loadZine = useAsyncTask(async (zineId: string) => {
        const data = await getZine({ id: zineId, filter: { include: ["focusAreas", "restrictedStrategicPartners"] } });
        const agencyRegions = agency?.regionsMapping.map((i) => i.value) || [];
        const activeRegions = data.regions?.filter((i) => agencyRegions.includes(i));
        if (activeRegions.length) setAppliedFilter({ regions: activeRegions[0], focusAreaIds: [], languages: [], type: [] });
    });
    useEffect(() => {
        if (zine?.id) {
            const { allowed } = checkDomains(zine);
            setIsDomainAllowed(allowed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.email, zine?.id]);
    useEffect(() => {
        if (zineId) {
            loadZine.run(zineId);
            return () => {
                setZine(undefined);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zineId]);
    useEffect(() => {
        if (guestInfo) {
            return () => {
                setGuestInfo(undefined);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guestInfo]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const authNeeded = useMemo(() => !!zine?.isAuthRequired, [zine?.isAuthRequired]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const previewNeeded = useMemo(() => !!zine?.isPreviewResource, [zine?.isPreviewResource]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const basicDetailsNeeded = useMemo(() => !!zine?.isDetailResource, [zine?.isDetailResource]);

    const getComponent = useCallback(() => {
        if (!zine) return <div />;
        if (previewNeeded || basicDetailsNeeded) {
            if (isAuthenticated || guestInfo) {
                return <Route {...routeProps} />;
            } else {
                return <EntityPreview entity={zine} />;
            }
        } else if (authNeeded) {
            if (isAuthenticated) {
                if (!isDomainAllowed) {
                    return <DomainNoAccessDialog redirectUrl={"/resources"} />;
                }
                if (zine && zine.isRegRequired) {
                    if (zine.registrationOpen) return <WithEntityRegistration entity={zine!} {...routeProps} />;
                    else return <DomainNoAccessDialog titleText={"Registrations are closed for this resource."} redirectUrl={"/resources"} />;
                } else return <Route {...routeProps} />;
            } else return <EntityPreview entity={zine} />;
        } else {
            return <Route {...routeProps} />;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isDomainAllowed, authNeeded, zine?.isRegRequired, AuthComponent, previewNeeded, guestInfo]);
    if (loadZine.status === "ERROR") return <Redirect to={`/resources`} />;
    return (
        <>
            <ResourceMeta resource={zine} />
            {loadZine.status === "PROCESSING" ? (
                <FullScreenLoader />
            ) : (
                <>
                    {loadZine.status === "SUCCESS" && getComponent()}
                    {loadZine.status === "SUCCESS" && !authNeeded && !previewNeeded && !basicDetailsNeeded ? <AuthRecommender /> : null}
                </>
            )}
        </>
    );
};

export default ZineRoute;
